/**
 * Pure gameplay tuning constants. No engine/runtime imports.
 * All distances are in CSS pixels; all speeds in px/second; times in ms.
 */

export const PLAYER = {
  /** Radius of the player ball (px). */
  radius: 28,
  /**
   * How far above the pointer the ball sits so a finger doesn't cover it on
   * touch devices (px). The ball eases toward (pointerX, pointerY - offset).
   */
  pointerOffsetY: 56,
  /**
   * Easing factor applied each fixed step when chasing the target (0..1).
   * Higher = snappier, lower = floatier. Tuned for the 60 Hz fixed step.
   */
  followLerp: 0.22,
  /** Idle pupil orbit speed (radians/second) — the "googly eye" wander. */
  eyeOrbitSpeed: 5.0,
  /**
   * Distance (px) at which the eyes start tracking the nearest obstacle. The
   * closer the danger, the more strongly the pupils lock onto it.
   */
  eyeLookRange: 320,
} as const;

export const SIM = {
  /** Fixed simulation timestep (ms) → 60 Hz logic regardless of render rate. */
  fixedDeltaMs: 1000 / 60,
  /**
   * Hard clamp on wall-time consumed in a single frame (ms). Prevents the
   * spiral-of-death and large time jumps after the tab is backgrounded.
   */
  maxFrameMs: 250,
} as const;

export const SCORE = {
  /** Points awarded per second survived (owner spec: a clean 1 point/second). */
  perSecond: 1,
} as const;

/**
 * The CANONICAL play field EVERY run is simulated in, regardless of device. Fixing the
 * viewport (instead of using the raw device canvas) is what makes play FAIR across
 * screens: a 16:9 desktop and a 9:16 phone face the IDENTICAL field — same attack
 * geometry AND the same ball-to-field ratio (the ball radius is absolute px, so a
 * fixed resolution keeps it consistent). The renderer scales this field to fit the
 * device and ROTATES it 90° on portrait so the landscape field fills a tall screen;
 * input is mapped back into these coordinates. The {@link import('./types').RunRecord}
 * stores this viewport so anti-cheat/replay re-simulate the same field.
 *
 * Changing this changes the field for everyone ⇒ BUMP {@link SIM_VERSION}.
 */
export const CANONICAL_VIEWPORT = { width: 1280, height: 720 } as const;

/**
 * Crusher tuning (the `walls` family). A wall attack is ALWAYS two walls crushing in
 * from OPPOSITE sides — left+right or top+bottom — covering the field except a
 * survivable LANE between them (seeded-offset). Each wall CREEPS in slowly over the
 * telegraph (the teaser — read which way to dodge), CRUSHES fast to closed at the start
 * of the active window, HOLDS shut, then OPENS back out. The split fractions partition
 * the ACTIVE window. See {@link import('./patterns/walls')} + {@link import('./world').stepWorld}.
 */
export const WALL = {
  /**
   * Width of the survivable LANE between the two walls at full closure, in canonical px
   * (an ABSOLUTE width — NOT a span fraction — so the wide left/right pair and the
   * narrow top/bottom pair leave the same comfortable gap, and the walls genuinely
   * crush). Must stay well above the ball diameter (56px) for fair play.
   */
  laneWidth: 180,
  /**
   * Fraction of a wall's final cover that has slid in by the end of the slow CREEP.
   * Kept LOW so the creep only POKES the wall in (telegraphing the side) and the fast
   * crush then SLAMS the rest shut — a dramatic, readable close.
   */
  creepFraction: 0.34,
  /** Fraction of the ACTIVE window spent in the FAST crush (creep → closed slam). */
  crushFraction: 0.18,
  /** Fraction of the ACTIVE window the wall HOLDS fully closed (lethal squeeze). */
  holdFraction: 0.46,
  // (The remaining 1 − crushFraction − holdFraction of the active window is the OPEN.)
} as const;

/**
 * The mounted laser turret's solid BODY is lethal (not just its beam) while firing —
 * touching the turret ends the run. `pad` is how far the body is clamped IN from the
 * edge (MUST match the renderer's mount in src/game/TurretFx.ts); `radius` is its
 * lethal hit radius (kept a touch under the art so it's forgiving).
 */
export const TURRET_BODY = {
  pad: 30,
  radius: 24,
} as const;

/**
 * The caro's spinning quad-turret HUB is solid + lethal while live (its centre is a
 * no-go zone, like the turret body). Radius scales with the beam length and matches
 * the rendered hub in src/game/CaroFx.ts.
 */
export const CARO = {
  hubRadiusFraction: 0.042,
  hubMinRadius: 24,
} as const;

/**
 * Until the run reaches this speed step (i.e. for the first three speed-ups), only
 * ONE attack runs at a time — the scheduler waits for the current attack to fully
 * clear before starting the next, so the early game reads cleanly. From this step
 * on, attacks may overlap (intended escalation). See {@link import('./world').stepWorld}.
 */
export const SOLO_ATTACK_BELOW_STEP = 3;
/** A calm beat (ms) inserted between sequential attacks while solo-attack is enforced. */
export const ATTACK_GAP_MS = 300;

export const SPAWN = {
  /** Spawn interval at t=0 (ms) and the floor it ramps down to (ms). */
  startIntervalMs: 900,
  minIntervalMs: 220,
  /** Time over which the interval eases from start → min (ms). */
  rampDurationMs: 60_000,

  /** Obstacle linear speed range (px/s). */
  minSpeed: 140,
  maxSpeed: 320,

  /** Obstacle square side length range (px). */
  minSize: 22,
  maxSize: 46,

  /** Max simultaneous live obstacles — also the pool capacity. */
  poolSize: 64,

  /** Spawn this far outside the viewport edge (px). */
  edgeMargin: 60,
} as const;

/**
 * Simulation version. BUMP whenever core sim logic changes in a way that would
 * make an old {@link import('./types').RunRecord} replay/score differently. The
 * server stamps validated runs with this so stale records aren't misjudged.
 */
export const SIM_VERSION = 9;

/**
 * One difficulty tier. Ported from legacy Avoid! (see
 * docs/legacy/AVOID_LEGACY_ANALYSIS.md §1.2). `attackSpeedMs` is how long a
 * telegraphed attack takes to resolve (lower = faster/harder); `spacingMs` is the
 * delay between attacks. Tiers escalate by *time survived*, not by the displayed
 * score (which is decoupled via {@link SCORE.perSecond}).
 */
export interface DifficultyTier {
  /** Difficulty level (1-based = speed step + 1); drives pattern weighting + turret count. */
  level: number;
  /** Telegraph/charge/active timing (ms) — lower = faster attacks, less reaction time. */
  attackSpeedMs: number;
  /** Delay between attacks (ms) — lower = more frequent. */
  spacingMs: number;
}

/**
 * Speed ramp (owner spec). The attack SPEED steps up at these ELAPSED marks, then
 * every {@link SPEED_REPEAT_MS} after the last one; each step multiplies the rate by
 * {@link SPEED_MULTIPLIER} (i.e. divides the timings), floored + capped so it can't
 * become degenerate. Driven by ELAPSED TIME (not the displayed score) so it's
 * identical client/server — see {@link import('./difficulty').speedStepForElapsed}.
 *
 * These are CUMULATIVE wall-clock marks built from the owner's interval spec —
 * "first after 20s, then 30s more, then 40s more, then a minute more, then 2 min
 * more, then every 2 min": 20 → 50 → 90 → 150 → 270 (seconds), then +120s forever.
 */
export const SPEED_THRESHOLDS_MS: readonly number[] = [20_000, 50_000, 90_000, 150_000, 270_000];
/** After the last fixed threshold (270s), speed steps up again every this many ms. */
export const SPEED_REPEAT_MS = 120_000;
/** Hard cap on the number of speed steps (the curve plateaus here, ~10 min in). */
export const MAX_SPEED_STEP = 9;
/** Each speed step multiplies the attack rate by this (≈1.3 felt "too much"; 1.28 is close). */
export const SPEED_MULTIPLIER = 1.28;
/** Base timings at speed step 0 (before the first speed-up). */
export const BASE_ATTACK_SPEED_MS = 2200;
export const BASE_SPACING_MS = 3800;
/** Floors so the fastest tiers stay playable + the spawn loop never degenerates. */
export const MIN_ATTACK_SPEED_MS = 300;
export const MIN_SPACING_MS = 520;
